import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, ref } from 'vue';
import urljoin from 'url-join';
import store from '@/store';
import { hasIntegrationAccess } from '@/api/user';
import SidebarMenuItem from './SidebarMenuItem.vue';
import SidebarUserCard from './SidebarUserCard.vue';
import CustomilyLogo from '@/assets/images/customily-logo.svg';
import CustomilyFullLogo from '@/assets/images/customily-full-logo.svg';
import { customily2StoreTypes } from '@/typings/storeType';
import { Roles, StoreType } from '@/typings/user';
import type { SidebarChildItem, SidebarMenuItem as SidebarMenuItemType, SidebarMenuSection } from './sidebarMenu';


export default /*#__PURE__*/_defineComponent({
  __name: 'Sidebar',
  props: {
    activeRoute: null,
    hasAccountsToImpersonate: { type: Boolean }
  },
  emits: ["navigate", "logout", "impersonate", "impersonate-child"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'navigate', path: string): void;
    (e: 'logout'): void;
    (e: 'impersonate'): void;
    (e: 'impersonate-child'): void;
}), expose: any, slots: any, attrs: any }) {





const isCollapsed = ref(false);
const toggleCollapsed = () => {
    isCollapsed.value = !isCollapsed.value;
    expandedItem.value = null;
};

const expandedItem = ref<string | null>(null);
const onToggle = (name: string) => {
    if (isCollapsed.value) {
        isCollapsed.value = false;
        expandedItem.value = name;
        return;
    }
    expandedItem.value = expandedItem.value === name ? null : name;
};

const onNavigate = (item: SidebarChildItem) => {
    if (item.route) {
        emit('navigate', item.route);
        return;
    }

    item.action?.();
};

const userRoles = computed(() => {
    const user = store.state.user;
    const roles = user?.AccessRoles?.Serialized.split(',') || [];
    return new Set(roles);
});

const userAccess = computed(() => {
    const user = store.state.user;
    const access = {
        customily2: false,
        store: false,
        orders: false,
        billing: false,
        podBilling: false
    };
    let hasBilling = false;
    for (const { Type: type, HasBilling, HasPODBilling, Disabled } of user?.Stores || []) {
        access.customily2 ||= customily2StoreTypes.includes(type) && !Disabled;
        access.orders ||=
            [StoreType.Amazon, StoreType.Shopify, StoreType.WooCommerce, StoreType.Etsy].includes(type) ||
            (type === StoreType.Custom && userRoles.value.has(Roles.CUSTOM_STORE));
        access.store = access.customily2 || access.orders || type === StoreType.Amazon;
        access.podBilling ||= HasPODBilling;

        hasBilling ||= HasBilling;
        if (HasBilling || (type === StoreType.Shopify && !HasBilling)) {
            access.billing = true;
        }
    }
    access.podBilling = !hasBilling && access.podBilling;
    return access;
});

const sections = computed(() => {
    const user = store.state.user;
    const sections: SidebarMenuSection[] = [
        {
            hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
            items: [
                {
                    name: 'Home',
                    icon: ['far', 'house'],
                    route: userAccess.value.customily2 ? '/store/podSelection/' : '/store',
                    hidden: !userAccess.value.store && !userAccess.value.customily2
                }
            ]
        },
        {
            name: 'Create',
            hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
            items: [
                {
                    name: 'Libraries',
                    icon: ['far', 'swatchbook'],
                    route: '/libraries',
                    hidden: !user?.ShowLibraries
                },
                {
                    name: 'Designs',
                    icon: ['far', 'brush'],
                    route: '/designs',
                    hidden: !userAccess.value.customily2
                },
                {
                    name: 'Product Bases',
                    icon: ['far', 'shirt'],
                    route: '/myProductBases',
                    hidden: !userAccess.value.customily2
                },
                {
                    name: 'POD Product Bases',
                    icon: ['far', 'shirt'],
                    route: '/pod/productBases',
                    hidden: !userRoles.value.has(Roles.EXTERNAL_POD_USER)
                },
                {
                    name: 'Templates',
                    icon: ['far', 'pen-paintbrush'],
                    route: '/home',
                    hidden: userRoles.value.size == 1 && userRoles.value.has(Roles.AFFILIATE)
                },
                {
                    name: 'Option Sets',
                    icon: ['far', 'list-tree'],
                    route: '/store/sets/',
                    hidden: !userAccess.value.store
                }
            ]
        },
        {
            name: 'Manage',
            items: [
                {
                    name: 'Products',
                    icon: ['far', 'tags'],
                    route: '/store/products/',
                    hidden: !userAccess.value.store
                },
                {
                    name: 'Orders',
                    icon: ['far', 'receipt'],
                    route: '/store/orders/',
                    hidden: !userAccess.value.orders
                },
                {
                    name: 'Store Settings',
                    icon: ['far', 'store'],
                    route: '/store/settings/',
                    hidden: !userAccess.value.store
                },
                {
                    name: 'Manage Options',
                    icon: ['far', 'list-tree'],
                    route: '/options',
                    hidden: !user?.ShowCustomilyOptions || userRoles.value.has(Roles.COLLECTIVE_RETAIL)
                },
                {
                    name: 'Affiliates',
                    icon: ['far', 'circle-dollar'],
                    route: '/affiliates',
                    hidden: !userRoles.value.has(Roles.AFFILIATE)
                },
                {
                    name: 'Admin',
                    icon: ['far', 'screwdriver-wrench'],
                    hidden:
                        user?.ImpersonatedFromChildUser ||
                        (!userRoles.value.has(Roles.HANGFIRE) && !userRoles.value.has(Roles.ADMIN)),
                    children: [
                        {
                            name: 'Hangfire Jobs',
                            hidden: !userRoles.value.has(Roles.HANGFIRE),
                            href: '/hangfire'
                        },
                        {
                            name: 'Bullmq Jobs',
                            hidden: !userRoles.value.has(Roles.HANGFIRE),
                            href: urljoin(process.env.VUE_APP_SHOPIFY_APP_URL, '/admin/bullmq')
                        },
                        {
                            name: 'Admin',
                            hidden: !(userRoles.value.has(Roles.ADMIN) && userRoles.value.has(Roles.ADMIN_UI)),
                            route: '/admin'
                        }
                    ]
                }
            ]
        }
    ];
    return sections
        .map((section) => ({
            ...section,
            items: section.items?.filter((item) => !item.hidden)
        }))
        .filter((section) => section.items?.length && !section.hidden);
});

const accountSection = computed<SidebarMenuItemType[]>(() => {
    const user = store.state.user;
    return [
        {
            name: 'Account',
            icon: ['far', 'circle-user'],
            children: [
                {
                    name: 'Settings',
                    route: '/account'
                },
                {
                    name: 'Integrations',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        (user?.ImpersonatedFromChildUser && !canManageIntegrations.value) ||
                        (userRoles.value.has(Roles.AFFILIATE) && !user?.Stores.some((s) => !s.Disabled)),
                    route: '/account/integrations'
                },
                {
                    name: 'Billing',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        user?.ImpersonatedFromChildUser ||
                        !userAccess.value.billing,
                    route: '/account/billing'
                },
                {
                    name: 'POD Billing',
                    hidden:
                        userRoles.value.has(Roles.COLLECTIVE_RETAIL) ||
                        user?.ImpersonatedFromChildUser ||
                        !userAccess.value.podBilling,
                    route: '/account/podBilling'
                },
                {
                    name: 'Team',
                    hidden: userRoles.value.has(Roles.COLLECTIVE_RETAIL),
                    route: '/account/team'
                }
            ]
        },
        {
            name: 'Help Center',
            icon: ['far', 'headset'],
            href: 'https://help.customily.com/hc/en-us'
        },
        {
            name: 'Log Out',
            icon: ['far', 'arrow-right-from-bracket'],
            action: () => emit('logout')
        }
    ];
});

const canManageIntegrations = ref(true);
const loadPermissionsData = async () => {
    const response = await hasIntegrationAccess();
    canManageIntegrations.value = response;
};

onBeforeMount(() => {
    loadPermissionsData();
});

return { __sfc: true,emit, isCollapsed, toggleCollapsed, expandedItem, onToggle, onNavigate, userRoles, userAccess, sections, accountSection, canManageIntegrations, loadPermissionsData, SidebarMenuItem, SidebarUserCard, CustomilyLogo, CustomilyFullLogo }
}

})